import type { GameDefinition, Outcome, Rng, Seat, Viewer } from '@campfire/engine';
import { shuffle } from '@campfire/cards';

export const UNO_COLORS = ['red', 'yellow', 'green', 'blue'] as const;
export type UnoColor = (typeof UNO_COLORS)[number];
export type UnoValue = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | 'skip' | 'reverse' | 'draw2' | 'wild' | 'wild4';

export interface UnoCard {
  readonly id: string;
  /** null for wilds; the chosen colour lives in state.color. */
  readonly color: UnoColor | null;
  readonly value: UnoValue;
}

export interface UnoOptions {
  /** Points to win the match; null = a single round. */
  readonly target: number | null;
  /** Forgetting to call UNO when down to one card costs two cards. */
  readonly unoPenalty: boolean;
  /** Wild Draw Four only when you hold nothing of the current colour. */
  readonly strictWild4: boolean;
}

export type UnoEvent =
  | { readonly kind: 'play'; readonly seat: Seat; readonly card: UnoCard; readonly color: UnoColor; readonly victim: Seat | null; readonly drew: number; readonly penalty: boolean; readonly uno: boolean }
  | { readonly kind: 'draw'; readonly seat: Seat; readonly playable: boolean; readonly card: UnoCard | null }
  | { readonly kind: 'pass'; readonly seat: Seat }
  | { readonly kind: 'round'; readonly winner: Seat; readonly points: number; readonly round: number };

export interface UnoState {
  readonly options: UnoOptions;
  readonly hands: readonly (readonly UnoCard[])[];
  /** Index 0 is the top. */
  readonly drawPile: readonly UnoCard[];
  /** Index 0 is the face-up top card. */
  readonly discard: readonly UnoCard[];
  readonly color: UnoColor;
  readonly turn: Seat;
  readonly direction: 1 | -1;
  /** A just-drawn playable card the player may play or keep. */
  readonly pending: string | null;
  readonly dealer: Seat;
  readonly round: number;
  readonly scores: readonly number[];
  readonly last: UnoEvent | null;
  readonly result: Outcome | null;
}

export type UnoAction =
  | { readonly type: 'play'; readonly card: string; readonly color?: UnoColor; readonly uno?: boolean }
  | { readonly type: 'draw' }
  | { readonly type: 'pass' };

export interface UnoView {
  readonly you: Seat | null;
  readonly hand: readonly UnoCard[] | null;
  /** Ids in your hand you may play right now. */
  readonly playable: readonly string[];
  readonly handCounts: readonly number[];
  readonly top: UnoCard;
  readonly color: UnoColor;
  readonly turn: Seat;
  readonly direction: 1 | -1;
  readonly drawCount: number;
  /** The card you just drew and may play (only shown to you). */
  readonly pending: string | null;
  readonly round: number;
  readonly scores: readonly number[];
  readonly target: number | null;
  readonly last: UnoEvent | null;
  readonly result: Outcome | null;
}

export function unoDeck(): UnoCard[] {
  const cards: UnoCard[] = [];
  for (const color of UNO_COLORS) {
    cards.push({ id: `${color}-0`, color, value: '0' });
    for (const value of ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'skip', 'reverse', 'draw2'] as const) {
      for (const k of [0, 1]) cards.push({ id: `${color}-${value}-${k}`, color, value });
    }
  }
  for (let k = 0; k < 4; k++) {
    cards.push({ id: `wild-${k}`, color: null, value: 'wild' });
    cards.push({ id: `wild4-${k}`, color: null, value: 'wild4' });
  }
  return cards;
}

export const cardPoints = (c: UnoCard): number =>
  c.value === 'wild' || c.value === 'wild4' ? 50 : c.value === 'skip' || c.value === 'reverse' || c.value === 'draw2' ? 20 : Number(c.value);

const isNumber = (c: UnoCard) => /^\d$/.test(c.value);
const top = (s: UnoState) => s.discard[0] as UnoCard;
const step = (s: UnoState, from: Seat, k: number): Seat => (((from + s.direction * k) % s.hands.length) + s.hands.length) % s.hands.length;

export function canPlay(s: UnoState, hand: readonly UnoCard[], c: UnoCard): boolean {
  if (c.value === 'wild') return true;
  if (c.value === 'wild4') return !s.options.strictWild4 || !hand.some((h) => h.color === s.color);
  return c.color === s.color || c.value === top(s).value;
}

/** Shuffle, deal 7 each, and turn up the first number card to start the discard. */
function deal(options: UnoOptions, seats: number, dealer: Seat, round: number, scores: readonly number[], rng: Rng): Omit<UnoState, 'last' | 'result'> {
  const deck = shuffle(unoDeck(), rng);
  const hands = Array.from({ length: seats }, (_, i) => deck.slice(i * 7, i * 7 + 7));
  const rest = deck.slice(seats * 7);
  const first = rest.findIndex(isNumber);
  const starter = rest[first] as UnoCard;
  return {
    options,
    hands,
    drawPile: rest.filter((_, i) => i !== first),
    discard: [starter],
    color: starter.color as UnoColor,
    turn: (dealer + 1) % seats,
    direction: 1,
    pending: null,
    dealer,
    round,
    scores,
  };
}

/** Draw `n` cards for `seat`, reshuffling the discard (all but its top) when the pile runs out. */
function drawCards(s: UnoState, seat: Seat, n: number, rng: Rng): { state: UnoState; drawn: UnoCard[] } {
  let pile = s.drawPile;
  let discard = s.discard;
  const drawn: UnoCard[] = [];
  for (let i = 0; i < n; i++) {
    if (pile.length === 0) {
      if (discard.length <= 1) break;
      pile = shuffle(discard.slice(1), rng);
      discard = discard.slice(0, 1);
    }
    drawn.push(pile[0] as UnoCard);
    pile = pile.slice(1);
  }
  const hands = s.hands.map((h, i) => (i === seat ? [...h, ...drawn] : h));
  return { state: { ...s, hands, drawPile: pile, discard }, drawn };
}

function endRound(s: UnoState, winner: Seat, rng: Rng): UnoState {
  const points = s.hands.reduce((sum, h) => sum + h.reduce((p, c) => p + cardPoints(c), 0), 0);
  const scores = s.scores.map((v, i) => (i === winner ? v + points : v));
  const target = s.options.target;
  if (target === null) {
    return { ...s, scores, result: { winners: [winner], draw: false, reason: 'out', scores: { ...scores } } };
  }
  if ((scores[winner] ?? 0) >= target) {
    return { ...s, scores, result: { winners: [winner], draw: false, reason: 'target', scores: { ...scores } } };
  }
  const next = deal(s.options, s.hands.length, (s.dealer + 1) % s.hands.length, s.round + 1, scores, rng);
  return { ...next, last: { kind: 'round', winner, points, round: s.round }, result: null };
}

export const uno: GameDefinition<UnoState, UnoAction, UnoView, UnoOptions> = {
  id: 'uno',
  name: 'Uno',
  rules: [
    'Be first to get rid of all your cards. Each player is dealt 7 and a number card is turned up to start.',
    'A turn is: play a card that matches the top card by colour, number or symbol, or play a wild. If you can’t, or don’t want to, draw one card.',
    'If the card you draw can be played you may play it right away, or keep it and pass. You may draw even if you have a playable card.',
    'Skip makes the next player lose a turn. Reverse flips the direction (it acts as a skip with two players). Draw 2 makes the next player draw 2 and lose their turn.',
    'Wild lets you pick the colour. Wild Draw Four picks the colour too and makes the next player draw 4 and lose their turn. The app does not allow stacking draw cards or jumping in.',
    'Setting: Wild Draw Four. By default only when you hold no card of the current colour; the app checks this, so there are no challenges. Choose Any time to allow it freely.',
    'Setting: Calling UNO. When you play to one card left, choose to call UNO. By default forgetting costs you 2 cards, applied automatically; choose No penalty to turn that off. Nobody else can catch you.',
    'Scoring: the round winner scores the cards left in everyone else’s hands (numbers face value, skip, reverse and draw 2 are 20, wilds 50).',
    'Setting: Game length. One round, or first to 250 or 500 points. When the draw pile runs out the discards are reshuffled.',
  ],
  minSeats: 2,
  maxSeats: 10,
  defaultOptions: { target: null, unoPenalty: true, strictWild4: true },
  optionSpecs: [
    {
      key: 'target',
      label: 'Game length',
      help: 'The round winner scores the cards left in everyone else’s hands.',
      choices: [
        { value: null, label: 'One round' },
        { value: 250, label: 'First to 250 points' },
        { value: 500, label: 'First to 500 points' },
      ],
    },
    {
      key: 'unoPenalty',
      label: 'Calling UNO',
      choices: [
        { value: true, label: 'Forget to call it: draw 2' },
        { value: false, label: 'No penalty' },
      ],
    },
    {
      key: 'strictWild4',
      label: 'Wild Draw Four',
      choices: [
        { value: true, label: 'Only with no card of the current colour' },
        { value: false, label: 'Any time' },
      ],
    },
  ],

  setup({ seats, options, rng }) {
    return { ...deal(options, seats, 0, 1, Array.from({ length: seats }, () => 0), rng), last: null, result: null };
  },

  activeSeats: (s) => (s.result ? [] : [s.turn]),

  validate(s, a, seat) {
    if (seat !== s.turn) return { ok: false, reason: 'not your turn' };
    const hand = s.hands[seat] ?? [];
    if (a.type === 'draw') return s.pending ? { ok: false, reason: 'play the card you drew or keep it' } : { ok: true };
    if (a.type === 'pass') return s.pending ? { ok: true } : { ok: false, reason: 'draw a card first' };
    if (a.type !== 'play') return { ok: false, reason: 'unknown action' };
    const card = hand.find((c) => c.id === a.card);
    if (!card) return { ok: false, reason: 'you do not hold that card' };
    if (s.pending && card.id !== s.pending) return { ok: false, reason: 'you may only play the card you just drew' };
    if (!canPlay(s, hand, card)) {
      return { ok: false, reason: card.value === 'wild4' ? 'Wild Draw Four only when you have no card of the current colour' : 'that card does not match' };
    }
    if (card.color === null && !UNO_COLORS.includes(a.color as UnoColor)) return { ok: false, reason: 'choose a colour' };
    return { ok: true };
  },

  apply(s, a, { rng }) {
    const seat = s.turn;
    if (a.type === 'draw') {
      const { state, drawn } = drawCards(s, seat, 1, rng);
      const card = drawn[0] ?? null;
      const playable = card !== null && canPlay(state, state.hands[seat] ?? [], card);
      const last: UnoEvent = { kind: 'draw', seat, playable, card };
      return playable ? { ...state, pending: (card as UnoCard).id, last } : { ...state, turn: step(state, seat, 1), last };
    }
    if (a.type === 'pass') return { ...s, pending: null, turn: step(s, seat, 1), last: { kind: 'pass', seat } };

    const card = (s.hands[seat] as readonly UnoCard[]).find((c) => c.id === a.card) as UnoCard;
    const color = card.color ?? (a.color as UnoColor);
    const hand = (s.hands[seat] as readonly UnoCard[]).filter((c) => c.id !== card.id);
    let st: UnoState = { ...s, hands: s.hands.map((h, i) => (i === seat ? hand : h)), discard: [card, ...s.discard], color, pending: null };
    if (hand.length === 0) {
      return endRound({ ...st, last: { kind: 'play', seat, card, color, victim: null, drew: 0, penalty: false, uno: false } }, seat, rng);
    }
    const penalty = hand.length === 1 && s.options.unoPenalty && !a.uno;
    if (penalty) st = drawCards(st, seat, 2, rng).state;
    let victim: Seat | null = null;
    let drew = 0;
    let turn: Seat;
    if (card.value === 'reverse') {
      st = { ...st, direction: st.direction === 1 ? -1 : 1 };
      turn = st.hands.length === 2 ? seat : step(st, seat, 1);
    } else if (card.value === 'skip') {
      turn = step(st, seat, 2);
    } else if (card.value === 'draw2' || card.value === 'wild4') {
      victim = step(st, seat, 1);
      const res = drawCards(st, victim, card.value === 'draw2' ? 2 : 4, rng);
      st = res.state;
      drew = res.drawn.length;
      turn = step(st, seat, 2);
    } else {
      turn = step(st, seat, 1);
    }
    return { ...st, turn, last: { kind: 'play', seat, card, color, victim, drew, penalty, uno: hand.length === 1 && !!a.uno } };
  },

  view(s, viewer: Viewer) {
    const you = viewer.kind === 'seat' ? viewer.seat : null;
    const hand = you === null ? null : (s.hands[you] ?? null);
    const mine = you !== null && you === s.turn && !s.result;
    const playable = mine && hand ? hand.filter((c) => (s.pending ? c.id === s.pending : true) && canPlay(s, hand, c)).map((c) => c.id) : [];
    const last = s.last;
    // A drawn card is private to the drawer.
    const shownLast = last?.kind === 'draw' && last.seat !== you ? { ...last, card: null } : last;
    return {
      you,
      hand,
      playable,
      handCounts: s.hands.map((h) => h.length),
      top: top(s),
      color: s.color,
      turn: s.turn,
      direction: s.direction,
      drawCount: s.drawPile.length,
      pending: mine ? s.pending : null,
      round: s.round,
      scores: s.scores,
      target: s.options.target,
      last: shownLast,
      result: s.result,
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (s.result || seat !== s.turn) return [];
    const hand = s.hands[seat] ?? [];
    const out: UnoAction[] = [];
    for (const c of hand) {
      if (s.pending && c.id !== s.pending) continue;
      if (!canPlay(s, hand, c)) continue;
      const uno = hand.length === 2;
      if (c.color === null) for (const color of UNO_COLORS) out.push({ type: 'play', card: c.id, color, uno });
      else out.push({ type: 'play', card: c.id, uno });
    }
    out.push(s.pending ? { type: 'pass' } : { type: 'draw' });
    return out;
  },
};
